import React from 'react';
import { Grid, Tooltip } from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/Header';
import { LoadingScreen } from '../../components/LoadingScreen';
import { ImageViewer } from '../../components/ImageViewer';
import { AnswerFieldValue } from '../../types/AnswerFieldValueType';
import { TextField, DropdownField, ValuesField, CounterField, DateField, FieldTypeTitle, ItemTitle } from './FieldsStyles';
import { useTranslation } from 'react-i18next';

interface AnswerPageProps {
  answerFieldValue: AnswerFieldValue[];
  isLoading: boolean;
  contractId?: string;
  projectId?: string;
}

export function AnswerPage({ answerFieldValue, isLoading, contractId, projectId = '' }: AnswerPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const radioTypeArray = answerFieldValue.filter(item => item.type === 'radio');
  const checkboxTypeArray = answerFieldValue.filter(item => item.type === 'checkbox');
  const dropdownTypeArray = answerFieldValue.filter(item => item.type === 'dropdown');
  const imageTypeArray = answerFieldValue.filter(item => item.type === 'image');
  const textTypeArray = answerFieldValue.filter(item => item.type === 'text');
  const counterTypeArray = answerFieldValue.filter(item => item.type === 'counter');
  const dateTypeArray = answerFieldValue.filter(item => item.type === 'datetime');

  return (
    <>
      {isLoading ? (
        <LoadingScreen isVisible={isLoading} />
      ) : (
        <>
          <Header />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', gap: 30, marginLeft: 100, marginTop: 30 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 30, flex: 1 }} >
              <Tooltip title='Voltar'>
                <ArrowLeft onClick={() => navigate(`/contracts/${contractId}/project/${projectId}/answers`)} size={30} color='#FF9E45' cursor='pointer' />
              </Tooltip>
              <span style={{ fontWeight: 'bold', color: '#7A7A7A' }}>{t('project-records-answers-title')}</span>
            </div>
          </div>

          {imageTypeArray.length > 0 && (
            <div style={{ marginLeft: 100, marginTop: 30 }}>
              <ImageViewer
                imageAnswerValues={imageTypeArray}
                projectId={projectId}
              />
            </div>
          )}

          <div style={{ width: '90%', display: 'flex', gap: 20, marginLeft: 100, marginTop: 30, marginBottom: 30 }}>
            <Grid container style={{ display: 'flex', flexDirection: 'row', gap: 20, }}>
              <>
                {answerFieldValue?.length < 1 && (
                  <div style={{ width: '100vw', height: '55vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <h3>Esse projeto não possui registros. 😢</h3>
                  </div>
                )}

                <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: 20 }}>
                  {textTypeArray.length > 0 && (
                    <Grid container style={{ marginRight: 100, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='TEXTO' />
                      <Grid container style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                        {textTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <ItemTitle children={item.title} />
                              <TextField
                                value={item?.answer_field_value?.value}
                                readOnly
                              />
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {dropdownTypeArray.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='DROPDOWN' />
                      <Grid container style={{ display: 'flex', marginRight: 100, flexDirection: 'row', gap: 10 }}>
                        {dropdownTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <ItemTitle children={item.title} />
                              <DropdownField
                                value={item?.answer_field_value?.value}
                                readOnly
                              />
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </div>
                  )}

                  {checkboxTypeArray.length > 0 && (
                    <Grid container style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='CHECKBOX' />
                      <Grid container style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                        {checkboxTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <ItemTitle children={item.title} />
                              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px', width: '10rem', gap: 10 }}>
                                <ValuesField
                                  type='checkbox'
                                  checked
                                  readOnly
                                />
                                <span style={{ fontSize: '14px' }}>
                                  {item?.answer_field_value?.value}
                                </span>
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {radioTypeArray.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='RADIO' />
                      <Grid container style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                        {radioTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <ItemTitle children={item.title} />
                              <div style={{ display: 'flex', width: '10rem', gap: 10, marginTop: '10px' }}>
                                <ValuesField
                                  type='radio'
                                  checked
                                  readOnly
                                />
                                <span style={{ fontSize: '14px' }}>
                                  {item?.answer_field_value?.value}
                                </span>
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </div>
                  )}

                  {counterTypeArray.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='CONTADOR' />
                      <Grid container style={{ display: 'flex', marginRight: 100, flexDirection: 'row', gap: 10 }}>
                        {counterTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <ItemTitle children={item.title} />
                              <CounterField value={item.answer_field_value.value} readOnly />
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </div>
                  )}

                  {dateTypeArray.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <FieldTypeTitle children='DATA/HORA' />
                      <Grid container style={{ display: 'flex', marginRight: 100, flexDirection: 'row', gap: 10 }}>
                        {dateTypeArray.map(item => (
                          <React.Fragment key={item.answer_field_value?.id}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <ItemTitle children={item.title} />
                              <DateField value={item.answer_field_value.value} readOnly />
                            </div>
                          </React.Fragment>
                        ))}
                      </Grid>
                    </div>
                  )}
                </div>
              </>
            </Grid>
          </div>
        </>
      )}
    </>
  );
}
